import React, {useContext, useEffect, useRef, useState} from 'react';
import {
    Box,
    Button,
    Col,
    ComponentLoader,
    ErrorSummary,
    FormCheckbox, IconChevronLeft,
    Modal,
    Row,
    TextLink,
    tokens,
} from '@snsw-gel/react';
import { pxToRem } from '@snsw-gel/theming';
import TollReliefConfig from '../../config/TollReliefConfig';
import DefaultNoStepper from 'src/components/Layout/DefaultNoStepper';
import withLayout from 'src/components/Layout/WithLayout';
import {ButtonsContainer, scrollToTop} from '@gaas/gaas-react-component-library';
import {useHistory} from 'react-router-dom';
import {LinkedAccount, ProductData, TollRegistrationContext,} from '../../context/ApplicationContext';
import config from '../../config/config';
import routes from '../../routes'
import TollAccountType from 'src/types/tollAccountType';
import {saveRegistrationApi, TollAccountFailure, unlinkAccount} from '../../services/api-services'
import useTRConfigs from 'src/hooks/useTrConfig';
import {isErrorRedirect} from 'src/services/httpClient/responseInterceptor';
import useTrRegistrationForm from 'src/hooks/useTrRegistrationForm';
import {composeProductData, displayableTollProvider} from 'src/utils/helpers';
import useStartTransaction from 'src/hooks/useStartTransaction';
import {ErrorSummaryError} from 'src/types/kiama';
import ApiErrorAlert from './ApiErrorAlert';
import {useCookies} from 'react-cookie';
import moment from 'moment';
import {userInfo} from 'src/utils/sessionStorage';
import { EventData, generateEventData } from 'src/utils/ga-page-tracking';
import {FeatureToggleName, useBaseFeature} from '../../hooks/features/useBaseFeature';

interface IProps {
    openRemoveAccountModal?: boolean
}

export const ERROR_TC_NOT_CHECKED = 'Read and accept the terms and conditions to continue.';
export const ERROR_SUMMARY_TC_NOT_CHECKED = 'Read and accept the terms and conditions.';

// TODO remove this page after tr2 decomm. User will simply go to MTA page so this is not needed
const TermsConditionsTr3Page: React.FC<IProps> = (props: IProps) => {
    const history = useHistory();
    const [isTermsConditionsChecked, setIsTermsConditionsChecked] = useState<boolean>(false);
    const [isRemoveAccountModalOpen, setIsRemoveAccountModalOpen] = useState<boolean>(false);
    const [showCheckboxesError, setShowCheckboxesError] = useState<boolean>(false);
    const [isCallingApi, setIsCallingApi] = useState<boolean>(false);
    const {
        linkedTollAccounts,
        eligibleTr2Account,
        setEligibleTr2Account,
    } = useContext(TollRegistrationContext);
    const [trProductData, setTrProductData] = useState<ProductData>({});

    const trRegistrationForm = useTrRegistrationForm();
    const transaction = useStartTransaction(history);

    const [errorSummary, setErrorSummary] = useState<ErrorSummaryError[]>([]);
    const errorSummaryRef = useRef<HTMLDivElement>(null);

    /** For api errors */
    const [apiErrorReason, setApiErrorReason] = useState<TollAccountFailure | undefined>(undefined);

    const userInfoObj = userInfo();

    const {isFeatureEnabled, isFeatureQueryLoading} = useBaseFeature();
    const [isTr2Decommissioned, setIsTr2Decommissioned] = useState<boolean>(false);

    useEffect(() => {
        if (userInfoObj && userInfoObj.id) {
            // Simulating data for the event
            const eventData: EventData = {
                page_title: 'Link toll account - Personal',
                transaction_info: {
                    transaction_step: 'New Toll Relief Rebate Scheme',
                },
                user_info: {
                    user_id: userInfoObj.id,
                    user_login_status: 'fully authenticated',
                },
            };

            // Generating and pushing the event data to the dataLayer
            generateEventData(eventData);
        }
    }, [userInfoObj]);

    /**
     * OPTIONS to preset state, mostly used for testing
     */
    useEffect(() => {
        if (props.openRemoveAccountModal) {
            setIsRemoveAccountModalOpen(props.openRemoveAccountModal);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /**
     * PRE-CHECK
     */
    useEffect(() => {
        // Require linkedTollAccounts or otherwise should go to MTA
        if (!Array.isArray(linkedTollAccounts) || linkedTollAccounts.length < 1) {
            history.push(routes.manageTollAccount.path);
            return;
        }
        // Require a personal / mixed account
        const eligibleAccountTypes = [TollAccountType.Personal, TollAccountType.Mixed]
        const eligibleAccount = linkedTollAccounts.find((it) => eligibleAccountTypes.includes(it.tollAccountType));
        if (!eligibleAccount) {
            history.push(routes.manageTollAccount.path);
            return;
        }
        setEligibleTr2Account(eligibleAccount);
        const productData = composeProductData(eligibleAccount, {agreeToTr3PersonalTerms: isTermsConditionsChecked});
        setTrProductData(productData);

        if (!trRegistrationForm.isValid(productData)) {
            // we know early that the toll account is not valid (missing some details), directly display error alert
            setApiErrorReason(TollAccountFailure.tollAccountDetailsInvalid);
        }

        /**
         * Set Gaas transaction
         */
        const initialiseTransaction = async () => {
            await transaction.initialise();
        }
        if (!transaction.isInitialised()) {
            initialiseTransaction();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if(!isFeatureQueryLoading && isFeatureEnabled(FeatureToggleName.TR2_DECOMMISSION)) {
            setIsTr2Decommissioned(true);
            history.push(routes.manageTollAccount.path);
            return;
        }
    }, [history, isFeatureQueryLoading, isFeatureEnabled]);

    const onRemoveTollAccount = () => {
        setIsRemoveAccountModalOpen(true);
    }

    const onRemoveTollAccountConfirmation = async () => {
        if (!eligibleTr2Account) {
            console.error('account is not defined for removal');
            return;
        }
        try {
            await unlinkAccount(eligibleTr2Account);
            history.push(routes.manageTollAccount.path);
        } catch (error: any) {
            if (isErrorRedirect(error)) {
                console.error(`unlink account failed: ${error.statusText}`);
                history.push(error.redirect);
            } else {
                console.error(`unlink account failed: ${error}`);
                history.push(routes.errorPage.path);
            }
        }
    }

    const saveTr3TermsDecision = async (accepted: boolean) => {
        return new Promise<void>((resolve, reject) => {
            try {
                const expiresAtDate= moment()
                    .add(TollReliefConfig.tr3TermsDecisionCookie.monthsValidFor, 'months')
                    .toDate();

                setCookie(TollReliefConfig.tr3TermsDecisionCookie.name, accepted ? 'true': 'false',
                    {
                        path: '/',
                        secure: true,
                        sameSite: 'strict',
                        expires: expiresAtDate});
                resolve();
            } catch(e) {
                reject(e);
            }
        });
    }

    const onTermsConditionsCheckboxChange = () => {
        setIsTermsConditionsChecked(!isTermsConditionsChecked);
        setTrProductData(composeProductData(eligibleTr2Account!, {agreeToTr3PersonalTerms: !isTermsConditionsChecked}));
        setShowCheckboxesError(false);
    }

    const onAcceptTermsConditions = async () => {
        if (isCallingApi) {
            return;
        }

        if (!eligibleTr2Account) {
            console.error('account is not defined for linking');
            return;
        }
        if (!isTermsConditionsChecked) {
            setShowCheckboxesError(true);
            return;
        }
        if (!trRegistrationForm.isValid(trProductData)) {
            console.error('trRegistrationForm is not valid');
            return;
        }

        try {
            setIsCallingApi(true);
            const potentialFailure = await saveRegistrationApi(
                trRegistrationForm.data(
                    {
                        ...trProductData,
                        agreeTerms: {
                            ...trProductData.agreeTerms,
                            // Infer that they agree to tr2 terms as they already should have an account (already accepted them)
                            agreeToTr2PersonalTerms: true,
                            // Explicitly overriding any sole trader terms they could have accepted on a different page.
                            agreeToTr2SoleTraderTerms: false
                        },
                    })
            );
            setIsCallingApi(false);
            if (potentialFailure === null) { // success
                try {
                    transaction.clear();
                    await saveTr3TermsDecision(true);
                    window.location.assign(TollReliefConfig.urls.tollTrackerURL);
                } catch (e) {
                    console.error('Error caught saving cookie', e);
                    history.push('/error/500');
                }
            } else if (potentialFailure in TollAccountFailure) {
                setApiErrorReason(potentialFailure as TollAccountFailure);
                scrollToTop();
            } else {
                // TODO: what if its some other error? that isn't a TollAccountFailure
            }

        } catch (error: any) {
            if (isErrorRedirect(error)) {
                console.error(`accept TC failed: ${error.statusText}`);
                history.push(error.redirect);
            } else {
                console.error(`accept TC failed: ${error}`);
                history.push(routes.errorPage.path);
            }
        }
    }

    const onContinueWithoutAgreeConfirmation = async () => {
        try {
            await saveTr3TermsDecision(false);
            window.location.assign(TollReliefConfig.urls.tollTrackerURL);
        } catch (e) {
            console.error('Error caught saving cookie', e);
            history.push('/error/500');
        }
    }

    const trConfig = useTRConfigs();

    /**
     * MODALS
     */
    const removeAccountModal = (account: LinkedAccount) =>
        <Modal
            id='removeAccountModal'
            title={`Remove ${displayableTollProvider(account.tollProvider)} account ${account.tollAccountNumber}?`}
            buttons={[
                {text: 'Remove account', onClick: onRemoveTollAccountConfirmation},
                {
                    text: 'Cancel', onClick: () => {
                        setIsRemoveAccountModalOpen(false);
                    }
                },
            ]}
        >
            <p>You will not be able to view toll spend or claim a rebate for a toll account which is not linked to your Toll
                Tracker.</p>
        </Modal>

    const [, setCookie] = useCookies([TollReliefConfig.tr3TermsDecisionCookie.name])
    useEffect(() => {
        let errors: ErrorSummaryError[] = [];
        if (!isTermsConditionsChecked && showCheckboxesError) {
            errors = [{
                id: `${TollReliefConfig.anchors['agreeToTr3PersonalTerms']}`,
                text: ERROR_SUMMARY_TC_NOT_CHECKED,
            }];
        }
        setErrorSummary(errors);
    }, [isTermsConditionsChecked, showCheckboxesError])

    useEffect(() => {
        if (errorSummary.length) {
            errorSummaryRef?.current?.focus();
        }
    }, [errorSummary]);

    const displayTermsCondition = () => {
        if (apiErrorReason !== undefined) {
            return <></>;
        } else {
            return <>
                <Row>
                    <Col span={8}>
                        <hr/>
                    </Col>
                </Row>
                <Row>
                    <Col span={8}>
                        <h3>Terms and Conditions</h3>
                        <p>By accepting and continuing you agree to the new{' '}
                            <TextLink href={config.links.termsAndConditionsLink}
                                target='_blank'
                                rel='noreferrer noopener'
                            >terms and conditions</TextLink>
                            {' '}for the {trConfig.getTR3weeklyCap()} weekly cap rebate scheme.
                        </p>
                        <p>How your privacy is handled is explained in the{' '}
                            <TextLink href={config.links.privacyCollectionNoticeLink}
                                target='_blank'
                                rel='noreferrer noopener'
                            >privacy collection notice</TextLink>.
                        </p>
                        <p>If you decline, you can still claim under the {trConfig.getTR3weeklyCap()} weekly cap rebate scheme at a later time.</p>
                        <FormCheckbox
                            id={TollReliefConfig.anchors['agreeToTr3PersonalTerms']}
                            name='new_tc_check'
                            label='I have read and accept the terms and conditions.'
                            value={isTermsConditionsChecked}
                            errorMessage={ERROR_TC_NOT_CHECKED}
                            hasError={showCheckboxesError && !isTermsConditionsChecked}
                            onChange={onTermsConditionsCheckboxChange}/>
                    </Col>
                </Row>
                <Row>
                    <Col span={8}>
                        <ButtonsContainer
                            style={{'display': 'flex', 'gap': '16px'}}
                        >
                            <Button
                                id='acceptButton'
                                onClick={onAcceptTermsConditions}
                                data-testid='acceptButton'
                            >
                                Continue
                            </Button>
                            <Button
                                id='continueButton'
                                onClick={onContinueWithoutAgreeConfirmation}
                                variant='secondary'
                                data-testid='continueButton'
                            >
                                Decline and go to Toll Tracker
                            </Button>
                        </ButtonsContainer>
                    </Col>
                </Row>
            </>;
        }
    };

    const displayHeadSection = () => {
        if (apiErrorReason === undefined) {
            return <Row>
                <Col span={8}>
                    <p>From 1 January 2024, you may be eligible to claim on 2 toll relief rebate schemes:</p>
                    <ul>
                        <li>new {trConfig.getTR3weeklyCap()} weekly cap scheme, and</li>
                        <li>current 40% rebate scheme.</li>
                    </ul>
                    <h4>About {trConfig.getTR3weeklyCap()} weekly cap rebate scheme</h4>
                    <p>If you spend from {trConfig.getTR3weeklyCap()} to $400 a week on tolls:</p>
                    <ul>
                        <li>You can get up to {trConfig.getTR3tagRebateCap()} per week back.</li>
                        <li>You can claim every 3 months.</li>
                    </ul>
                    <p>To start claiming on either or both schemes, check your linked toll account details
                        and accept the terms and conditions.</p>
                </Col>
            </Row>;
        } else {
            return <>
                <ApiErrorAlert errorReason={apiErrorReason}/>

                <Row>
                    <Col span={8}>
                        <p>From 1 January 2024 there is a new toll relief scheme.</p>
                        <p>You'll be able to</p>
                        <ul>
                            <li>receive a cash rebate of up to {trConfig.getTR3tagRebateCap()} per week if you spend over {trConfig.getTR3weeklyCap()} a week on tolls,</li>
                            <li>claim every 3 months,</li>
                            <li>claim a rebate under this and 40% rebate schemes.</li>
                        </ul>
                    </Col>
                </Row>
            </>;
        }
    }

    const showUnlinkRelinkButtonText = (apiErrorReason: TollAccountFailure | undefined) =>
        apiErrorReason && [TollAccountFailure.tollAccountDetailsInvalid,
                        TollAccountFailure.tspApiTollAccountNotFound,
                        TollAccountFailure.tomeeTollAccountNotFound].includes(apiErrorReason)

    return (
        <>
            {/** For use when api calls are in flight */}
            <ComponentLoader active={trConfig.isLoading ||
                transaction.isLoading ||
                isCallingApi ||
                isFeatureQueryLoading ||
                isTr2Decommissioned} fullPage
                             label={TollReliefConfig.labels.componentLoader}/>

            <Row>
                <Col span={8}>
                    <ErrorSummary errors={errorSummary} ref={errorSummaryRef}/>
                </Col>
            </Row>

            {displayHeadSection()}

            <Row>
                <Col span={8}>
                    <h3 style={{marginTop: '0'}}>Linked toll account</h3>
                </Col>
            </Row>
            <Row>
                <Col span={6}>
                    <Box style={{marginTop: '0'}}>
                        <h4>Personal toll account</h4>
                        <Row>
                            <Col span={5}>Toll provider</Col>
                            <Col span={7}>
                                <strong>{displayableTollProvider(eligibleTr2Account?.tollProvider)}</strong>
                            </Col>
                        </Row>
                        <Row style={{marginTop: `${pxToRem(tokens.spacing.sm)}`}}>
                            <Col span={5}>Toll account number</Col>
                            <Col span={7}>
                                <strong>{eligibleTr2Account?.tollAccountNumber}</strong>
                            </Col>
                        </Row>

                        <Button
                            style={{marginTop: `${pxToRem(tokens.spacing.md)}`}}
                            variant='secondary'
                            id='removeButton'
                            onClick={onRemoveTollAccount}
                        >
                            {showUnlinkRelinkButtonText(apiErrorReason) ? 'Unlink and relink' : 'Remove and add new toll account'}
                        </Button>
                    </Box>
                </Col>
            </Row>

            {displayTermsCondition()}

            <Button
                iconStart={<IconChevronLeft/>}
                style={{marginTop: `${pxToRem(tokens.spacing.sm)}`}}
                id='exitButton'
                href={config.links.tollReliefLandingPage}
                theme='link'
            >
                Exit
            </Button>
            {/* MODALS */}
            {isRemoveAccountModalOpen && eligibleTr2Account && removeAccountModal(eligibleTr2Account)}
        </>
    )
}

export default withLayout(TermsConditionsTr3Page, DefaultNoStepper, {headerTitle: 'New Toll Relief Rebate Scheme'});
